import React, { FC, useCallback } from 'react';
import dayjs from 'dayjs';
import { message, Image } from 'antd';
import { DocumentIcon, EditPencilIcon } from 'components/Elements/CustomSVGIcon';
import AuthImage from 'components/Elements/AuthImage';
import { fetchApiAuth } from 'core/utils/api';
import { DiaryEntry, DiaryEntryMedia } from 'core/incidents/types';
import './diaryEntryCard.scss';

interface DiaryEntryCardProps {
    entry: DiaryEntry;
    // When provided, an edit control is shown that lets the user correct this
    // entry's wording/details.
    // eslint-disable-next-line react/require-default-props
    onEdit?: ((entry: DiaryEntry) => void) | undefined;
}

// Timestamps within a couple of seconds of creation are just the initial save,
// not a genuine edit — only flag entries that were changed later.
const wasEdited = (entry: DiaryEntry): boolean => {
    if (!entry.updatedAt || !entry.createdAt) return false;
    return dayjs(entry.updatedAt).diff(dayjs(entry.createdAt), 'second') > 2;
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const isImageMedia = (mediaItem: DiaryEntryMedia): boolean => {
    const name = mediaItem.originalFilename || mediaItem.filename || '';
    const parts = name.split('.');
    if (parts.length <= 1) return false;
    const ext = parts[parts.length - 1];
    return !!ext && IMAGE_EXTENSIONS.includes(ext.toLowerCase());
};

const DiaryEntryCard: FC<DiaryEntryCardProps> = ({ entry, onEdit }) => {
    const handleDocumentOpen = useCallback(async (mediaItem: DiaryEntryMedia) => {
        try {
            const response = await fetchApiAuth({
                method: 'GET',
                responseType: 'blob',
                url: mediaItem.url,
            });

            const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
            if (!newWindow) {
                window.URL.revokeObjectURL(url);
                message.error('Popup blocked. Please allow popups to open the document.');
                return;
            }
            newWindow.opener = null;
            // Revoke after a delay to allow the new tab to load the blob
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch {
            message.error('Failed to open document. The file may be missing or access denied.');
        }
    }, []);

    const mediaItems = entry.media || [];
    const imageMedia = mediaItems.filter(isImageMedia);
    const documentMedia = mediaItems.filter((mediaItem) => !isImageMedia(mediaItem));

    return (
        <div className="diary-entry">
            {onEdit && (
                <button
                    type="button"
                    className="diary-edit-button"
                    onClick={() => onEdit(entry)}
                    aria-label="Edit diary entry">
                    <EditPencilIcon />
                </button>
            )}
            <p className="diary-text">{entry.text}</p>
            {imageMedia.length > 0 && (
                <Image.PreviewGroup>
                    <div className="diary-images">
                        {imageMedia.map((mediaItem) => (
                            <div className="diary-image-thumb" key={mediaItem.id}>
                                <AuthImage
                                    src={mediaItem.url}
                                    alt={mediaItem.originalFilename}
                                    width="100%"
                                    height="100%"
                                    style={{ objectFit: 'cover' }} />
                            </div>
                        ))}
                    </div>
                </Image.PreviewGroup>
            )}
            {documentMedia.map((mediaItem) => (
                <button
                    type="button"
                    className="diary-attachment"
                    key={mediaItem.id}
                    onClick={() => handleDocumentOpen(mediaItem)}>
                    <DocumentIcon w={12} h={15} />
                    <span>{mediaItem.originalFilename}</span>
                </button>
            ))}
            <div className="diary-meta">
                <span>{(entry.entryOccurredAt || entry.createdAt) ? dayjs(entry.entryOccurredAt || entry.createdAt).format('DD/MM/YYYY hh:mm A') : ''}</span>
                {entry.user?.name && <span className="diary-author">by {entry.user.name}</span>}
                {wasEdited(entry) && <span className="diary-edited">(edited)</span>}
            </div>
        </div>
    );
};

export default DiaryEntryCard;
